import React from "react";
import styled from "styled-components";
import { motion } from "framer-motion";
import {
  Users,
  Target,
  Award,
  BookOpen,
  Lightbulb,
  Heart,
  ArrowRight,
} from "lucide-react";

const AboutContainer = styled.div`
  min-height: 100vh;
  background: ${({ theme }) => theme.colors.gradients.background};
  padding-top: 6rem;
  overflow-x: hidden;
  width: 100%;
  position: relative;
`;

const HeroSection = styled.section`
  padding: 4rem 2rem 6rem;
  background: ${({ theme }) => theme.colors.surface.primary};
  text-align: center;
  overflow-x: hidden;
  width: 100%;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: 3rem 1.5rem 5rem;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    padding: 2.5rem 1rem 4rem;
  }
`;

const HeroContent = styled(motion.div)`
  max-width: ${({ theme }) => theme.container.lg};
  margin: 0 auto;
`;

const HeroTitle = styled(motion.h1)`
  font-size: clamp(2.5rem, 5vw, 3.5rem);
  font-weight: ${({ theme }) => theme.typography.weights.bold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: 1.5rem;
  line-height: 1.2;
`;

const HeroSubtitle = styled(motion.p)`
  font-size: clamp(1rem, 2.5vw, ${({ theme }) => theme.typography.sizes.xl});
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-bottom: 3rem;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.6;
  padding: 0 0.5rem;
`;

const StorySection = styled.section`
  padding: 6rem 2rem;
  background: ${({ theme }) => theme.colors.gradients.subtle};
  overflow-x: hidden;
  width: 100%;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: 5rem 1.5rem;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    padding: 4rem 1rem;
  }
`;

const StoryContainer = styled.div`
  max-width: ${({ theme }) => theme.container.lg};
  margin: 0 auto;
`;

const StoryGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  align-items: center;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    grid-template-columns: 1fr;
    gap: 3rem;
  }
`;

const StoryContent = styled(motion.div)``;

const StoryTitle = styled.h2`
  font-size: clamp(2rem, 4vw, 2.5rem);
  font-weight: ${({ theme }) => theme.typography.weights.bold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: 1.5rem;
`;

const StoryText = styled.p`
  font-size: ${({ theme }) => theme.typography.sizes.lg};
  color: ${({ theme }) => theme.colors.text.secondary};
  line-height: 1.7;
  margin-bottom: 1.5rem;
`;

const StoryVisual = styled(motion.div)`
  background: ${({ theme }) => theme.colors.gradients.primary};
  border-radius: ${({ theme }) => theme.borderRadius["2xl"]};
  padding: 3rem;
  text-align: center;
  color: ${({ theme }) => theme.colors.text.inverse};
  box-shadow: ${({ theme }) => theme.shadows.lg};
  position: relative;
  overflow: hidden;

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    padding: 2rem 1.5rem;
  }

  /* Enhanced contrast and readability */
  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.15);
    z-index: 1;
  }

  * {
    position: relative;
    z-index: 2;
    text-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
  }
`;

const ValuesSection = styled.section`
  padding: 6rem 2rem;
  background: ${({ theme }) => theme.colors.surface.primary};
  overflow-x: hidden;
  width: 100%;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: 5rem 1.5rem;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    padding: 4rem 1rem;
  }
`;

const ValuesContainer = styled.div`
  max-width: ${({ theme }) => theme.container.xl};
  margin: 0 auto;
`;

const SectionTitle = styled(motion.h2)`
  font-size: clamp(2rem, 4vw, 2.5rem);
  font-weight: ${({ theme }) => theme.typography.weights.bold};
  color: ${({ theme }) => theme.colors.text.primary};
  text-align: center;
  margin-bottom: 1rem;
`;

const SectionSubtitle = styled(motion.p)`
  font-size: ${({ theme }) => theme.typography.sizes.lg};
  color: ${({ theme }) => theme.colors.text.secondary};
  text-align: center;
  margin-bottom: 4rem;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
`;

const ValuesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 280px), 1fr));
  gap: 2rem;
`;

const ValueCard = styled(motion.div)`
  background: ${({ theme }) => theme.colors.surface.primary};
  border: 1px solid ${({ theme }) => theme.colors.surface.border};
  border-radius: ${({ theme }) => theme.borderRadius.xl};
  padding: 2.5rem;
  text-align: center;
  transition: all ${({ theme }) => theme.animations.duration.fast}
    ${({ theme }) => theme.animations.easing.ease};
  box-shadow: ${({ theme }) => theme.shadows.sm};

  &:hover {
    transform: translateY(-4px);
    box-shadow: ${({ theme }) => theme.shadows.lg};
    border-color: ${({ theme }) => theme.colors.accent.primary};
  }
`;

const ValueIcon = styled.div`
  width: 80px;
  height: 80px;
  margin: 0 auto 1.5rem;
  background: ${({ theme }) => theme.colors.gradients.secondary};
  border-radius: ${({ theme }) => theme.borderRadius.xl};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.text.inverse};
`;

const ValueTitle = styled.h3`
  font-size: ${({ theme }) => theme.typography.sizes.xl};
  font-weight: ${({ theme }) => theme.typography.weights.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: 1rem;
`;

const ValueDescription = styled.p`
  color: ${({ theme }) => theme.colors.text.secondary};
  line-height: 1.6;
`;

const TeamSection = styled.section`
  padding: 6rem 2rem;
  background: ${({ theme }) => theme.colors.gradients.subtle};
  overflow-x: hidden;
  width: 100%;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: 5rem 1.5rem;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    padding: 4rem 1rem;
  }
`;

const TeamContainer = styled.div`
  max-width: ${({ theme }) => theme.container.lg};
  margin: 0 auto;
`;

const TeamGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 2rem;
  margin-top: 3rem;
`;

const TeamMember = styled(motion.div)`
  background: ${({ theme }) => theme.colors.surface.primary};
  border-radius: ${({ theme }) => theme.borderRadius.xl};
  padding: 2rem;
  text-align: center;
  box-shadow: ${({ theme }) => theme.shadows.card};
  transition: all ${({ theme }) => theme.animations.duration.fast}
    ${({ theme }) => theme.animations.easing.ease};

  &:hover {
    transform: translateY(-4px);
    box-shadow: ${({ theme }) => theme.shadows.elevated};
  }
`;

const MemberAvatar = styled.div<{ hasImage?: boolean }>`
  width: 100px;
  height: 100px;
  margin: 0 auto 1.5rem;
  background: ${({ theme, hasImage }) =>
    hasImage ? "transparent" : theme.colors.gradients.primary};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.text.inverse};
  font-size: ${({ theme }) => theme.typography.sizes["2xl"]};
  font-weight: ${({ theme }) => theme.typography.weights.bold};
  overflow: hidden;
  border: 3px solid ${({ theme }) => theme.colors.surface.border};

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 50%;
  }
`;

const MemberName = styled.h3`
  font-size: ${({ theme }) => theme.typography.sizes.xl};
  font-weight: ${({ theme }) => theme.typography.weights.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: 0.5rem;
`;

const MemberRole = styled.p`
  color: ${({ theme }) => theme.colors.accent.primary};
  font-weight: ${({ theme }) => theme.typography.weights.medium};
  margin-bottom: 1rem;
`;

const MemberBio = styled.p`
  color: ${({ theme }) => theme.colors.text.secondary};
  line-height: 1.6;
  font-size: ${({ theme }) => theme.typography.sizes.sm};
`;

const CTASection = styled.section`
  padding: 6rem 2rem;
  background: ${({ theme }) => theme.colors.surface.primary};
  text-align: center;
  overflow-x: hidden;
  width: 100%;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: 5rem 1.5rem;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    padding: 4rem 1rem;
  }
`;

const CTAContainer = styled.div`
  max-width: ${({ theme }) => theme.container.lg};
  margin: 0 auto;
`;

const CTATitle = styled(motion.h2)`
  font-size: clamp(2rem, 4vw, 2.5rem);
  font-weight: ${({ theme }) => theme.typography.weights.bold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: 1rem;
`;

const CTADescription = styled(motion.p)`
  font-size: ${({ theme }) => theme.typography.sizes.lg};
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-bottom: 3rem;
`;

const CTAButton = styled(motion.button)`
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem 2rem;
  background: ${({ theme }) => theme.colors.gradients.primary};
  color: ${({ theme }) => theme.colors.text.inverse};
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  font-size: ${({ theme }) => theme.typography.sizes.lg};
  font-weight: ${({ theme }) => theme.typography.weights.semibold};
  cursor: pointer;
  transition: all ${({ theme }) => theme.animations.duration.fast}
    ${({ theme }) => theme.animations.easing.ease};
  box-shadow: ${({ theme }) => theme.shadows.lg};

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${({ theme }) => theme.shadows.xl};
  }
`;

// Technologies Section - NEW!
const TechnologiesSection = styled.section`
  padding: 6rem 2rem;
  background: ${({ theme }) => theme.colors.surface.primary};
  overflow-x: hidden;
  width: 100%;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: 5rem 1.5rem;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    padding: 4rem 1rem;
  }
`;

const TechnologiesContainer = styled.div`
  max-width: ${({ theme }) => theme.container.xl};
  margin: 0 auto;
  text-align: center;
`;

const TechnologiesGrid = styled.div`
  display: flex;
  justify-content: center;
  align-items: flex-start;
  gap: 2rem;
  margin-top: 3rem;
  flex-wrap: wrap;

  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    gap: 1.5rem;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    gap: 1rem;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    flex-direction: column;
    align-items: center;
    gap: 2rem;
  }
`;

const TechCard = styled(motion.div)`
  background: ${({ theme }) => theme.colors.surface.primary};
  border: 1px solid ${({ theme }) => theme.colors.surface.border};
  border-radius: ${({ theme }) => theme.borderRadius.xl};
  padding: 1.5rem 1rem;
  text-align: center;
  transition: all ${({ theme }) => theme.animations.duration.fast}
    ${({ theme }) => theme.animations.easing.ease};
  box-shadow: ${({ theme }) => theme.shadows.sm};

  /* Fixed dimensions for consistency */
  width: 180px;
  height: 220px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  &:hover {
    transform: translateY(-4px);
    box-shadow: ${({ theme }) => theme.shadows.lg};
    border-color: ${({ theme }) => theme.colors.accent.primary};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    width: 200px;
    height: 240px;
    padding: 2rem 1.5rem;
  }
`;

const TechLogo = styled.img`
  width: 60px;
  height: 60px;
  object-fit: contain;
  margin: 0 auto 1rem;
  filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.1));
  transition: all ${({ theme }) => theme.animations.duration.fast}
    ${({ theme }) => theme.animations.easing.ease};
  flex-shrink: 0;

  ${TechCard}:hover & {
    filter: drop-shadow(0 6px 12px rgba(59, 130, 246, 0.2));
    transform: scale(1.05);
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    width: 70px;
    height: 70px;
  }
`;

const TechName = styled.h4`
  font-size: ${({ theme }) => theme.typography.sizes.base};
  font-weight: ${({ theme }) => theme.typography.weights.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: 0.5rem;
  flex-shrink: 0;
  line-height: 1.2;
`;

const TechDescription = styled.p`
  font-size: ${({ theme }) => theme.typography.sizes.xs};
  color: ${({ theme }) => theme.colors.text.secondary};
  line-height: 1.4;
  flex-grow: 1;
  display: flex;
  align-items: center;
  text-align: center;
`;

const About: React.FC = () => {
  const values = [
    {
      icon: <Target size={40} />,
      title: "Student-Centered Focus",
      description:
        "Every solution we create is designed with student engagement and learning outcomes at the center of our decision-making process.",
    },
    {
      icon: <Lightbulb size={40} />,
      title: "Innovation in Education",
      description:
        "We constantly push the boundaries of what's possible in educational technology, turning complex data into intuitive experiences.",
    },
    {
      icon: <Heart size={40} />,
      title: "Educational Impact",
      description:
        "We measure our success by the positive impact our solutions have on students, teachers, and educational communities.",
    },
    {
      icon: <Award size={40} />,
      title: "Technical Excellence",
      description:
        "Our commitment to quality code, security, and performance ensures reliable solutions that districts can trust.",
    },
  ];

  const teamMembers = [
    {
      name: "Chris Swinford",
      role: "Owner & Data Science Administrator",
      bio: "Professional software developer with over 20 years of experience architecting backend data systems for large-scale educational organizations. Expert in SQL, PostgreSQL, and enterprise database solutions.",
      initials: "CS",
      image: "/images/team/chris-swinford.jpg",
    },
    {
      name: "Neil Wheeler",
      role: "Software Developer",
      bio: "Frontend developer specializing in modern UI/UX design and AI-assisted development. Expert at leveraging cutting-edge tools and technologies to create engaging, user-centered interfaces that follow the latest design trends and best practices.",
      initials: "NW",
      image: "/images/team/neil-wheeler.jpg",
    },
  ];

  const technologies = [
    {
      name: "Scorpious3",
      logo: "/scorpious3.jpg",
      description:
        "Proprietary data application powering custom APIs and backend data systems",
    },
    {
      name: "Core-API",
      logo: "/core-api.png",
      description:
        "Transform your development workflow with Core API - where cutting-edge technology meets productivity.",
    },
    {
      name: "Microsoft SQL Server",
      logo: "https://www.vectorlogo.zone/logos/microsoft/microsoft-icon.svg",
      description:
        "Enterprise-grade relational database for large-scale educational data systems",
    },
    {
      name: "PostgreSQL",
      logo: "https://www.vectorlogo.zone/logos/postgresql/postgresql-icon.svg",
      description:
        "Advanced open-source database with powerful features for complex queries",
    },
    {
      name: "MongoDB",
      logo: "https://www.vectorlogo.zone/logos/mongodb/mongodb-icon.svg",
      description:
        "Flexible NoSQL database perfect for educational content management",
    },
    {
      name: "DuckDB",
      logo: "https://upload.wikimedia.org/wikipedia/commons/4/40/DuckDB_logo.svg",
      description:
        "High-performance analytical database for fast data processing",
    },
    {
      name: "Redis",
      logo: "https://www.vectorlogo.zone/logos/redis/redis-icon.svg",
      description:
        "In-memory data store for caching and real-time applications",
    },
  ];

  return (
    <AboutContainer>
      <HeroSection>
        <HeroContent
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <HeroTitle
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            Transforming Education Through Technology
          </HeroTitle>

          <HeroSubtitle
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            We're passionate about creating technology that makes learning more
            engaging, data more actionable, and educational outcomes more
            achievable.
          </HeroSubtitle>
        </HeroContent>
      </HeroSection>

      <StorySection>
        <StoryContainer>
          <StoryGrid>
            <StoryContent
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <StoryTitle>Our Story</StoryTitle>
              <StoryText>
                Founded in 2020, Swintek emerged from a simple observation:
                educational data was becoming increasingly complex, but the
                tools to make it useful were falling behind.
              </StoryText>
              <StoryText>
                Our founders, coming from backgrounds in both education and
                technology, recognized that students learn best when they're
                engaged, and educators perform best when they have clear,
                actionable insights.
              </StoryText>
              <StoryText>
                Today, we've helped over 50 school districts transform their
                data into platforms that students actually want to use, creating
                social media-style experiences that boost engagement while
                maintaining the highest standards of educational value.
              </StoryText>
            </StoryContent>

            <StoryVisual
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <BookOpen size={80} />
              <h3
                style={{
                  fontSize: "1.5rem",
                  marginTop: "1rem",
                  marginBottom: "1rem",
                  fontWeight: "bold",
                  color: "#ffffff",
                }}
              >
                50+ Districts Served
              </h3>
              <p
                style={{
                  fontSize: "1rem",
                  lineHeight: "1.6",
                  color: "#ffffff",
                  opacity: "0.95",
                }}
              >
                Transforming educational data into engaging experiences for over
                100,000 students nationwide.
              </p>
            </StoryVisual>
          </StoryGrid>
        </StoryContainer>
      </StorySection>

      <TechnologiesSection>
        <TechnologiesContainer>
          <SectionTitle
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            Technologies We Use
          </SectionTitle>

          <SectionSubtitle
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
          >
            Powering educational solutions with industry-leading database
            technologies
          </SectionSubtitle>

          <TechnologiesGrid>
            {technologies.map((tech, index) => (
              <TechCard
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ scale: 1.02 }}
              >
                <TechLogo
                  src={tech.logo}
                  alt={`${tech.name} logo`}
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = "none";
                  }}
                />
                <TechName>{tech.name}</TechName>
                <TechDescription>{tech.description}</TechDescription>
              </TechCard>
            ))}
          </TechnologiesGrid>
        </TechnologiesContainer>
      </TechnologiesSection>

      <ValuesSection>
        <ValuesContainer>
          <SectionTitle
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            Our Values
          </SectionTitle>

          <SectionSubtitle
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
          >
            The principles that guide everything we do
          </SectionSubtitle>

          <ValuesGrid>
            {values.map((value, index) => (
              <ValueCard
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ scale: 1.02 }}
              >
                <ValueIcon>{value.icon}</ValueIcon>
                <ValueTitle>{value.title}</ValueTitle>
                <ValueDescription>{value.description}</ValueDescription>
              </ValueCard>
            ))}
          </ValuesGrid>
        </ValuesContainer>
      </ValuesSection>

      <TeamSection>
        <TeamContainer>
          <SectionTitle
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            Meet Our Team
          </SectionTitle>

          <SectionSubtitle
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
          >
            Experienced professionals dedicated to educational excellence
          </SectionSubtitle>

          <TeamGrid>
            {teamMembers.map((member, index) => (
              <TeamMember
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <MemberAvatar hasImage={!!member.image}>
                  {member.image ? (
                    <img src={member.image} alt={member.name} />
                  ) : (
                    member.initials
                  )}
                </MemberAvatar>
                <MemberName>{member.name}</MemberName>
                <MemberRole>{member.role}</MemberRole>
                <MemberBio>{member.bio}</MemberBio>
              </TeamMember>
            ))}
          </TeamGrid>
        </TeamContainer>
      </TeamSection>

      <CTASection>
        <CTAContainer>
          <CTATitle
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            Ready to Transform Your District's Data?
          </CTATitle>

          <CTADescription
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
          >
            Let's discuss how we can help create engaging educational
            experiences for your students.
          </CTADescription>

          <CTAButton
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            viewport={{ once: true }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            Get Started Today
            <ArrowRight size={20} />
          </CTAButton>
        </CTAContainer>
      </CTASection>
    </AboutContainer>
  );
};

export default About;
